import * as React from 'react'
import type { PlantEquipment } from '@axilon-apps/domains-ontology-ui-state'
import { FlowTile } from './FlowTile'
import { computeFlowDagLayout, FLOW_DAG_NODE_H, FLOW_DAG_NODE_W } from './flow-dag-layout'

/**
 * Connected DAG of a line's direct equipment along `CONNECTS_TO` edges.
 * Same dagre + FlowTile canvas as `SublineFlowDag`, but tiles navigate
 * to equipment detail and optional `regime` is shown as subtitle detail.
 */
export interface EquipmentConnectsFlowDagProps {
  equipment: PlantEquipment[]
  connects: Array<{ source: string; target: string; regime?: string }>
}

export function EquipmentConnectsFlowDag({
  equipment,
  connects
}: EquipmentConnectsFlowDagProps): React.ReactElement {
  const markerId = React.useId()
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [containerW, setContainerW] = React.useState(0)

  React.useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = (): void => setContainerW(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const layout = React.useMemo(() => {
    const ids = equipment.map((eq) => eq.id)
    const edges = connects.map(({ source, target }) => ({ source, target }))
    const horizontal = computeFlowDagLayout(ids, edges, 'LR')
    if (containerW > 0 && horizontal.width > containerW) {
      return computeFlowDagLayout(ids, edges, 'TB')
    }
    return horizontal
  }, [connects, containerW, equipment])

  const byId = React.useMemo(() => new Map(equipment.map((eq) => [eq.id, eq])), [equipment])
  const regimeByPair = React.useMemo(() => {
    const map = new Map<string, string>()
    for (const edge of connects) {
      if (edge.regime) map.set(`${edge.source}\u0000${edge.target}`, edge.regime)
    }
    return map
  }, [connects])

  return (
    <div ref={containerRef}>
      <div
        data-testid="equipment-connects-flow-dag"
        className="relative"
        style={{ width: layout.width, height: layout.height }}
      >
        <svg
          className="absolute inset-0 text-ink-4"
          width={layout.width}
          height={layout.height}
          aria-hidden
        >
          <defs>
            <marker
              id={markerId}
              viewBox="0 0 8 8"
              refX="7"
              refY="4"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M0,0 L8,4 L0,8 z" fill="currentColor" />
            </marker>
          </defs>
          {layout.edges.map((edge) => {
            const [source, target] = edge.key.split('->')
            const regime = regimeByPair.get(`${source}\u0000${target}`)
            return (
              <path
                key={edge.key}
                d={edge.path}
                fill="none"
                stroke="currentColor"
                strokeWidth={1.25}
                markerEnd={`url(#${markerId})`}
              >
                <title>{regime ? `CONNECTS_TO (${regime})` : 'CONNECTS_TO'}</title>
              </path>
            )
          })}
        </svg>
        {layout.nodes.map(({ id, x, y }) => {
          const eq = byId.get(id)
          if (!eq) return null
          return (
            <div
              key={id}
              className="absolute overflow-hidden [&>a]:h-full [&>a]:w-full"
              style={{ left: x, top: y, width: FLOW_DAG_NODE_W, height: FLOW_DAG_NODE_H }}
            >
              <FlowTile
                kind="equipment"
                id={eq.id}
                label="Equipment"
                node={eq}
                name={eq.name}
                nameLayout="wrap"
                subtitle={eq.equipment_type}
                equipmentType={eq.equipment_type}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
